import { api } from "@/lib/apiClient";

export type BalanceSheetLine = {
  account_id: number;
  account_code: string;
  account_name: string;
  account_class: string;
  account_group: string | null;
  amount: number;
};

export type BalanceSheetSection = {
  key: string;
  label: string;
  lines: BalanceSheetLine[];
  total: number;
};

export type BalanceSheet = {
  filters: {
    as_at: string | null;
    hide_zero: boolean;
  };
  sections: BalanceSheetSection[];
  totals: {
    assets: number;
    liabilities: number;
    equity: number;
    liabilities_and_equity: number;
    net_income: number;
    balanced: boolean;
  };
};

type ApiBalanceSheet = {
  filters?: {
    as_at?: string | null;
    hide_zero?: boolean;
  };
  sections?: Array<Record<string, unknown>>;
  totals?: {
    assets?: number;
    liabilities?: number;
    equity?: number;
    liabilities_and_equity?: number;
    net_income?: number;
    balanced?: boolean;
  };
};

export type FetchBalanceSheetParams = {
  asAt?: string | null;
  hideZero?: boolean;
};

function queryParams(params: FetchBalanceSheetParams = {}) {
  return {
    ...(params.asAt ? { as_at: params.asAt } : {}),
    ...(params.hideZero ? { hide_zero: 1 } : {}),
  };
}

async function downloadBlob(url: string, params: FetchBalanceSheetParams, filename: string): Promise<void> {
  const response = await api.get(url, { params: queryParams(params), responseType: "blob" });
  const blob = new Blob([response.data], {
    type: response.headers["content-type"] || "application/octet-stream",
  });
  const href = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = href;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(href);
}

function mapLine(row: Record<string, unknown>): BalanceSheetLine {
  return {
    account_id: Number(row.account_id ?? 0),
    account_code: String(row.account_code ?? ""),
    account_name: String(row.account_name ?? ""),
    account_class: String(row.account_class ?? ""),
    account_group: (row.account_group as string | null) ?? null,
    amount: Number(row.amount ?? 0),
  };
}

function mapSection(row: Record<string, unknown>): BalanceSheetSection {
  const lines = Array.isArray(row.lines) ? row.lines.map((line) => mapLine(line as Record<string, unknown>)) : [];
  return {
    key: String(row.key ?? ""),
    label: String(row.label ?? ""),
    lines,
    total: Number(row.total ?? 0),
  };
}

export async function fetchBalanceSheet(params: FetchBalanceSheetParams = {}): Promise<BalanceSheet> {
  const response = await api.get<{ data: ApiBalanceSheet }>("/api/v1/accounts/balance-sheet", {
    params: queryParams(params),
  });
  const data = response.data?.data ?? {};
  return {
    filters: {
      as_at: data.filters?.as_at ?? null,
      hide_zero: Boolean(data.filters?.hide_zero),
    },
    sections: Array.isArray(data.sections) ? data.sections.map(mapSection) : [],
    totals: {
      assets: Number(data.totals?.assets ?? 0),
      liabilities: Number(data.totals?.liabilities ?? 0),
      equity: Number(data.totals?.equity ?? 0),
      liabilities_and_equity: Number(data.totals?.liabilities_and_equity ?? 0),
      net_income: Number(data.totals?.net_income ?? 0),
      balanced: Boolean(data.totals?.balanced),
    },
  };
}

export async function downloadBalanceSheetPdf(params: FetchBalanceSheetParams = {}): Promise<void> {
  const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
  await downloadBlob("/api/v1/accounts/balance-sheet/pdf", params, `balance-sheet-${stamp}.pdf`);
}

export async function downloadBalanceSheetExcel(params: FetchBalanceSheetParams = {}): Promise<void> {
  const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
  await downloadBlob("/api/v1/accounts/balance-sheet/excel", params, `balance-sheet-${stamp}.csv`);
}
